import React, { useMemo } from 'react';

import { Box, ProgressStepper, Tag, Text } from '@zillow/constellation-10';

import { STEP_TO_STEP_GROUP_MAP, SUB_STEP_MAP } from '../../../../containers/CreateListingPage/config';
import { LISTING_SUMMARY_DISPLAY_STATUS } from '../../../../constants/ListingSummaryConstants';

import { DEFAULT_STEP } from '../../../../containers/CreateListingPage';

import type { WizardStepType } from '../../../Wizard/types';

type Props = {
  status: keyof typeof LISTING_SUMMARY_DISPLAY_STATUS;
  step: string;
};

const renderStatusTag = (status = '') => {
  switch (status) {
    case LISTING_SUMMARY_DISPLAY_STATUS.PUBLISHING:
      return 'COMPLETE';

    // fallthrough
    case LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE:
    default:
      return 'INCOMPLETE';
  }
};

const ProgressStepperSummary = ({ status, step }: Props) => {
  /**
   * find a valid progress step key from within the possible step group
   * legacy listings might have invalid step keys they are resuming from
   */
  const progressStepKey = useMemo(() => {
    const validStep =
      (STEP_TO_STEP_GROUP_MAP[step.toUpperCase()] as WizardStepType) ||
      (STEP_TO_STEP_GROUP_MAP[DEFAULT_STEP] as WizardStepType);

    const { progressStepKey }: Pick<WizardStepType, 'progressStepKey'> = validStep;

    return progressStepKey;
  }, []);

  const { steps, activeStepIndex } = useMemo(() => {
    const keys = Array.from(SUB_STEP_MAP.keys());
    const activeIdx = keys.findIndex((key) => key === progressStepKey);

    const stepLabels = keys.map((_key, index) => ({
      label: '',
      ...(index < activeIdx && { tone: 'complete' }),
    }));

    return { steps: stepLabels, activeStepIndex: activeIdx };
  }, [progressStepKey]);

  const statusTag = renderStatusTag(status);

  return (
    <Box data-testid="ProgressStepperSummary" css={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        data-testid="PSS-HeaderContainer"
        css={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingBottom: '200',
        }}
      >
        <Text textStyle="body-bold">Listing setup</Text>
        <Tag tone="gray">{statusTag}</Tag>
      </Box>

      <Box data-testid="ProgressStepperContainer">
        <ProgressStepper
          activeStepIndex={activeStepIndex}
          steps={steps}
          labelAppearance="none"
          aria-label="Listing setup progress"
        />
      </Box>
    </Box>
  );
};

export default ProgressStepperSummary;
